import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import ScrollToTop from "@/components/feature/ScrollToTop";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<{ type: "success" | "error" | null; message: string }>({ type: null, message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus({ type: null, message: "" });

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get("phone_alt") as string || "").trim();
    if (honeypot) {
      setFormStatus({ type: "success", message: "留言已发送，我们会尽快回复！" });
      setSubmitting(false);
      return;
    }
    formData.delete("phone_alt");

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch("https://readdy.ai/api/form/d9n4u4sit35ii3jdloi0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus({ type: "success", message: "留言已发送，我们会尽快回复！" });
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "提交失败，请稍后重试";
        setFormStatus({ type: "error", message: serverMsg });
      }
    } catch {
      setFormStatus({ type: "error", message: "网络异常，请检查网络后重试" });
    } finally {
      setSubmitting(false);
    }
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      { "@type": "Question", name: "宅男视频需要注册才能观看吗？", acceptedAnswer: { "@type": "Answer", text: "不需要注册。宅男视频所有视频内容均可免费在线观看，无需注册或登录，打开即看。" } },
      { "@type": "Question", name: "视频播放为什么会卡顿？", acceptedAnswer: { "@type": "Answer", text: "宅男视频采用先进的CDN分发技术和智能码率自适应引擎，正常情况下播放流畅不卡顿。如遇卡顿，建议检查网络连接或切换至较低清晰度。" } },
      { "@type": "Question", name: "宅男视频支持哪些设备观看？", acceptedAnswer: { "@type": "Answer", text: "宅男视频全面支持PC端、平板、手机等设备，兼容主流浏览器，无需下载APP即可在线观看。" } },
      { "@type": "Question", name: "平台有哪些视频品牌资源？", acceptedAnswer: { "@type": "Answer", text: "宅男视频聚合91视频、97视频、51视频网站等优质数字品牌资源，涵盖高清专区、最新上线等多个分类，内容丰富多样。" } },
      { "@type": "Question", name: "视频画质最高支持多少？", acceptedAnswer: { "@type": "Answer", text: "平台支持4K超清画质播放，高清专区内的视频全部支持1080P及以上画质，畅享极致视觉体验。" } },
    ],
  };

  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "宅男视频",
    url: "https://zhainanvideo.com",
    description: "宅男视频提供91视频、97视频等数字品牌在线入口，免费高清播放，覆盖51视频网站及数字视频平台海量资源。",
    contactPoint: { "@type": "ContactPoint", contactType: "customer service", email: "support@zhainanvideo.com", availableLanguage: ["Chinese"] },
  };

  const aboutJsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于宅男视频 - 数字视频平台",
    description: "了解宅男视频品牌故事、平台优势、用户口碑及联系方式",
    url: "https://zhainanvideo.com/about",
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们 - 宅男视频品牌介绍与联系方式"
        description="了解宅男视频品牌故事与发展历程，查看用户口碑评价，获取联系方式。宅男视频 - 91视频97视频免费高清在线观看数字视频平台。"
        keywords="宅男视频,关于我们,品牌介绍,联系方式,91视频,97视频,数字视频平台"
        canonicalUrl="https://zhainanvideo.com/about"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "关于我们" }]} />
            </div>

            {/* Brand Introduction */}
            <section className="mb-14">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">关于宅男视频</h1>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-3xl mb-6">
                宅男视频成立于2022年，是一家专注于数字视频内容聚合与分发的在线平台。平台聚合91视频、97视频、51视频网站等优质数字品牌资源，为用户提供免费高清、流畅不缓冲的在线观看体验。
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
                <div className="p-6 bg-background-100 rounded-xl border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-xl mb-4">
                    <i className="ri-eye-line text-primary-600 text-lg"></i>
                  </div>
                  <h2 className="text-lg font-semibold text-foreground-950 mb-2">我们的愿景</h2>
                  <p className="text-foreground-600 text-sm leading-relaxed">打造全球领先的数字视频平台，让每一位用户都能畅享极致的观影体验。坚持免费高清路线，持续优化播放技术，让视频不卡顿成为行业标配。</p>
                </div>
                <div className="p-6 bg-background-100 rounded-xl border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-xl mb-4">
                    <i className="ri-rocket-line text-accent-600 text-lg"></i>
                  </div>
                  <h2 className="text-lg font-semibold text-foreground-950 mb-2">我们的使命</h2>
                  <p className="text-foreground-600 text-sm leading-relaxed">通过技术创新降低视频消费门槛，聚合优质数字品牌资源，让用户以最简单的方式获取最丰富的视频内容。</p>
                </div>
              </div>
            </section>

            {/* Platform Advantages */}
            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">平台优势</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { icon: "ri-speed-line", title: "极速流畅", desc: "CDN加速 + 智能码率自适应，播放无缓冲" },
                  { icon: "ri-4k-line", title: "免费高清", desc: "支持4K超清画质，完全免费无需付费" },
                  { icon: "ri-shield-check-line", title: "安全可靠", desc: "严格内容审核机制，保障用户安全" },
                  { icon: "ri-customer-service-2-line", title: "贴心服务", desc: "7x24小时客服支持，快速响应处理" },
                ].map((item, i) => (
                  <div key={i} className="p-5 bg-background-100 rounded-xl border border-background-200/70 text-center">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-xl mx-auto mb-3">
                      <i className={`${item.icon} text-primary-600 text-lg`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1.5">{item.title}</h3>
                    <p className="text-foreground-600 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Brand Timeline */}
            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">发展历程</h2>
              <div className="space-y-4">
                {[
                  { year: "2022", title: "平台创立", desc: "宅男视频正式上线，以'流畅不卡顿'为核心目标" },
                  { year: "2023", title: "品牌聚合", desc: "接入91视频、97视频、51视频网站等数字品牌资源" },
                  { year: "2024", title: "快速发展", desc: "月活跃用户突破千万，完成4K超清画质全面升级" },
                  { year: "2025", title: "持续创新", desc: "探索AI智能推荐、VR沉浸式体验等前沿技术方向" },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 items-start p-5 bg-background-100 rounded-xl border border-background-200/70">
                    <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center bg-primary-500 text-background-50 rounded-xl text-lg font-bold">{item.year}</div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground-950 mb-1">{item.title}</h3>
                      <p className="text-foreground-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* User Reviews */}
            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">用户口碑</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { name: "张先生", rating: 5, text: "用了宅男视频大半年了，播放真的很流畅，基本没遇到过卡顿。91视频和97视频的资源也很丰富，强烈推荐！" },
                  { name: "李女士", rating: 5, text: "画质清晰，操作简单，不用注册就能看，真的非常方便。高清专区的4K视频效果太棒了！" },
                  { name: "王同学", rating: 4, text: "作为学生党，免费高清这点太友好了。资源更新速度也快，每天都有新内容看。希望继续保持。" },
                ].map((review, i) => (
                  <div key={i} className="p-5 bg-background-100 rounded-xl border border-background-200/70">
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, idx) => (
                        <i key={idx} className={`text-sm ${idx < review.rating ? "ri-star-fill text-amber-500" : "ri-star-line text-foreground-300"}`}></i>
                      ))}
                    </div>
                    <p className="text-foreground-700 text-sm leading-relaxed mb-3">"{review.text}"</p>
                    <p className="text-foreground-500 text-xs font-medium">— {review.name}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">常见问题</h2>
              <div className="space-y-3">
                {[
                  { q: "宅男视频需要注册才能观看吗？", a: "不需要注册。宅男视频所有视频内容均可免费在线观看，无需注册或登录，打开即看。" },
                  { q: "视频播放为什么会卡顿？", a: "宅男视频采用先进的CDN分发技术和智能码率自适应引擎，正常情况下播放流畅不卡顿。如遇卡顿，建议检查网络连接或切换至较低清晰度。" },
                  { q: "平台有哪些视频品牌资源？", a: "宅男视频聚合91视频、97视频、51视频网站等优质数字品牌资源，涵盖高清专区、最新上线等多个分类。" },
                  { q: "支持哪些设备观看？", a: "全面支持PC端、平板、手机等设备，兼容主流浏览器，无需下载APP即可在线观看。" },
                  { q: "视频画质最高支持多少？", a: "平台支持4K超清画质播放，高清专区内的视频全部支持1080P及以上画质。" },
                ].map((faq, i) => (
                  <details key={i} className="group bg-background-100 rounded-xl border border-background-200/70 cursor-pointer">
                    <summary className="px-5 py-4 text-sm font-medium text-foreground-950 list-none flex items-center justify-between">
                      {faq.q}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-5 pb-4 text-foreground-600 text-sm leading-relaxed">{faq.a}</div>
                  </details>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">联系我们</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="p-6 bg-background-100 rounded-xl border border-background-200/70">
                  <h3 className="text-base font-semibold text-foreground-950 mb-4">联系方式</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-sm text-foreground-600">
                      <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-lg"><i className="ri-mail-line text-primary-600"></i></div>
                      <span>support@zhainanvideo.com</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-foreground-600">
                      <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-lg"><i className="ri-time-line text-primary-600"></i></div>
                      <span>客服时间：周一至周日 9:00-21:00</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-foreground-600">
                      <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-lg"><i className="ri-map-pin-line text-primary-600"></i></div>
                      <span>在线服务平台，随时随地为您服务</span>
                    </div>
                  </div>
                </div>

                <div className="p-6 bg-background-100 rounded-xl border border-background-200/70">
                  <h3 className="text-base font-semibold text-foreground-950 mb-4">在线留言</h3>
                  <form onSubmit={handleSubmit} className="space-y-3" data-readdy-form="">
                    <input type="text" name="name" placeholder="您的称呼" className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors" />
                    <input type="email" name="email" placeholder="邮箱地址" className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors" />
                    <textarea name="message" placeholder="请输入您的留言内容（500字以内）" rows={3} maxLength={500} className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"></textarea>
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="contact-honeypot" />
                    <button type="submit" disabled={submitting} className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-background-50 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap">
                      {submitting ? "发送中..." : "发送留言"}
                    </button>
                    {formStatus.type === "success" && (
                      <p className="text-green-600 text-xs text-center">{formStatus.message}</p>
                    )}
                    {formStatus.type === "error" && (
                      <p className="text-red-500 text-xs text-center">{formStatus.message}</p>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}